import React, { useEffect, useRef, useState } from "react"
import Hls from "hls.js"

export default function HLSPlayer({ url, veeaLicenseisValid }) {
	const videoRef = useRef(null)
	const [hlsLoading, setHlsLoading] = useState(true)
	const [error, setError] = useState("")

	const errorHandledRef = useRef(false)

	useEffect(() => {
		const video = videoRef.current
		if (!video || !url) return

		errorHandledRef.current = false
		setError("")

		if (veeaLicenseisValid == null || veeaLicenseisValid == undefined) {
			setError("Veea Construction Site status unknown - Contact Veea")
			setHlsLoading(false)
			return
		}
		if (veeaLicenseisValid == false) {
			setError("License disabled or expired - Contact Veea")
			setHlsLoading(false)
			return
		}

		let hls

		setHlsLoading(true)

		// Attach events first
		video.onplaying = () => {
			setHlsLoading(false)
		}

		video.onwaiting = () => {
			setHlsLoading(true)
		}

		// Handle native video errors if hls.js doesn't catch them
		video.onerror = () => {
			if (errorHandledRef.current) return

			errorHandledRef.current = true

			console.error("Native video error:", video.error)

			setHlsLoading(false)

			if (!video.error) {
				setError("An unknown error occurred during video playback. Contact Veea")
				return
			}

			switch (video.error.code) {
				case 1:
					setError("Playback aborted")
					break
				case 2:
					setError(
						"Network error - Stream unavailable. Check camera on VEEA Construction Site or Contact Veea."
					)
					break
				case 3:
					setError("Stream decoding error")
					break
				case 4:
					setError(
						"Stream not found or Access Denied - Try re adding camera on Design Studio POI or Contact Veea."
					)
					break
				default:
					setError("Unknown playback error")
			}
		}

		if (video.canPlayType("application/vnd.apple.mpegurl")) {
			video.src = url
			video.load()
		} else if (Hls.isSupported()) {
			hls = new Hls({
				lowLatencyMode: true,
				liveSyncDurationCount: 2,
				maxLiveSyncPlaybackRate: 1.5,
				backBufferLength: 60,
				maxBufferLength: 10,
				enableWorker: true
			})

			hls.attachMedia(video)

			hls.on(Hls.Events.MEDIA_ATTACHED, () => {
				hls.loadSource(url)
			})

			hls.on(Hls.Events.MANIFEST_PARSED, () => {
				video.play().catch(() => {})
			})

			hls.on(Hls.Events.ERROR, (event, data) => {
				if (errorHandledRef.current) return

				errorHandledRef.current = true

				console.warn("HLS fatal error:", data)

				// If response exists, check HTTP status
				const statusCode = data?.response?.code

				if (statusCode === 404) {
					setError(
						"Stream not found or Access Denied - Try re adding camera on Design Studio POI or Contact Veea."
					)
					setHlsLoading(false)
					hls.destroy()
					return
				}

				if (statusCode === 403) {
					setError("Access denied - License may be invalid or expired. Contact Veea")
					setHlsLoading(false)
					hls.destroy()
					return
				}

				if (!data?.fatal) return

				if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
					hls.startLoad()
				} else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
					hls.recoverMediaError()
				} else {
					hls.destroy()
					setHlsLoading(false)
					setError("An error occurred while loading the stream. Contact Veea")
				}
			})
		} else {
			console.error("HLS not supported in this browser")
			setError("HLS not supported in this browser")
			setHlsLoading(false)
		}

		return () => {
			video.onplaying = null
			video.onwaiting = null
			video.onerror = null

			if (hls) {
				try {
					hls.detachMedia()
				} catch {}
				try {
					hls.destroy()
				} catch {}
			}
		}
	}, [url, veeaLicenseisValid])

	return (
		<div style={{ position: "relative", width: "100%", height: "150px" }}>
			{error && (
				<div
					style={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						color: "#ff8b8b",
						fontSize: "12px",
						zIndex: 3,
						textAlign: "center",
						padding: "0 10px",
						width: "100%"
					}}
				>
					{error}
				</div>
			)}

			{hlsLoading && (
				<div
					style={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						color: "white",
						fontSize: "12px",
						zIndex: 2
					}}
				>
					Loading feed...
				</div>
			)}

			<video
				ref={videoRef}
				autoPlay
				muted
				playsInline
				disablePictureInPicture
				controlsList="nodownload nofullscreen noremoteplayback"
				style={{
					width: "100%",
					height: "150px",
					borderRadius: "6px",
					backgroundColor: "black"
				}}
			/>
		</div>
	)
}
